import {
  PlantTimesheetReport,
  TimesheetEmployeeRow,
} from "@interfaces/ITimesheetEmployeeRowDTO";
import { PlantOption, fetchPlants } from "@service/PlantService";
import {
  fetchPlantTimesheetReport,
  sendConfirmEmails,
} from "@service/TimesheetReportService";
import { addDays, asDate, toISODate } from "@utils/date";
import PlantTimesheetReportView from "@view/admin/plant_timesheet_report/PlantTimesheetReportView";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { SnackbarContext } from "@components/constant_components/providers/SnackbarProvider";
import { useAuth } from "@components/context/AuthContext";
import { errorTreatment } from "@utils/general";

type Report = PlantTimesheetReport | null;

function ensureSunday(iso: string): string {
  const d = asDate(iso);
  if (!d) return iso;
  const day = d.getDay(); // 0 = Sun
  if (day === 0) return iso;
  // move back to previous Sunday
  const back = addDays(d, -day);
  return toISODate(back) ?? iso;
}

function ensureSaturday(iso: string): string {
  const d = asDate(iso);
  if (!d) return iso;
  const day = d.getDay(); // 6 = Sat
  if (day === 6) return iso;
  // move forward/back to nearest Saturday by aligning to sunday+6
  const delta = 6 - day;
  const target = addDays(d, delta);
  return toISODate(target) ?? iso;
}

export default function PlantTimeSheetReport() {
  const { triggerSnackbar } = useContext(SnackbarContext);
  const { handleLogout } = useAuth();
  const [plants, setPlants] = useState<PlantOption[]>([]);
  const [plantId, setPlantId] = useState<number | undefined>(undefined);

  const [startDate, setStartDate] = useState<string>(() => {
    // default to most recent Sunday
    const today = new Date();
    const sunday = addDays(today, -today.getDay());
    return toISODate(sunday) ?? "";
  });

  const [endDate, setEndDate] = useState<string>(() => {
    const s = asDate(ensureSunday(toISODate(new Date())!));
    const e = s ? addDays(s, 6) : null;
    return e ? toISODate(e)! : "";
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<Report>(null);
  const [emailSendSuccess, setEmailSendSuccess] = useState(false);

  // load plants
  useEffect(() => {
    let live = true;
    fetchPlants()
      .then((opts) => {
        if (!live) return;
        setPlants(opts);
        if (opts.length && plantId === undefined) {
          setPlantId(opts[0].id);
        }
      })
      .catch(async (e) => {
        if (!live) return;
        setError("Failed to load plants.");
        if (await errorTreatment(e, handleLogout)) {
          triggerSnackbar("Failed to load plants. Please try again.");
          return;
        }
        console.error(e);
      });
    return () => {
      live = false;
    };
  }, []); // eslint-disable-line

  const handleStartChange = useCallback((iso: string) => {
    const sIso = ensureSunday(iso);
    setStartDate(sIso);
    const s = asDate(sIso);
    if (s) setEndDate(toISODate(addDays(s, 6)) ?? "");
  }, []);

  const handleEndChange = useCallback((iso: string) => {
    const eIso = ensureSaturday(iso);
    setEndDate(eIso);
    const e = asDate(eIso);
    if (e) setStartDate(toISODate(addDays(e, -6)) ?? "");
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!plantId || !startDate || !endDate) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPlantTimesheetReport({
        plant_id: plantId,
        start_date: startDate,
        end_date: endDate,
      });

      if (
        !data ||
        typeof data === "string" ||
        (data as any).status === "redirect"
      ) {
        await handleLogout();
        return;
      }

      setReport(data);
    } catch (e) {
      setError("Failed to load timesheet report.");
      if (await errorTreatment(e, handleLogout)) {
        triggerSnackbar("Failed to load timesheet report. Please try again.");
        return;
      }
    } finally {
      setLoading(false);
    }
  }, [plantId, startDate, endDate, handleLogout, triggerSnackbar]);

  const handleSendConfirm = useCallback(async () => {
    if (!plantId || !startDate || !endDate) return;
    setLoading(true);
    setError(null);
    setEmailSendSuccess(false);

    triggerSnackbar("Sending confirmation emails...");

    try {
      await sendConfirmEmails({
        plant_id: plantId,
        start_date: startDate,
        end_date: endDate,
      });

      setEmailSendSuccess(true);
      triggerSnackbar("Confirmation emails sent successfully!");
      setEmailSendSuccess(false);
    } catch (e) {
      setError("Failed to send confirmation emails.");
      setEmailSendSuccess(false);
      if (await errorTreatment(e, handleLogout)) {
        triggerSnackbar(
          "Failed to send confirmation emails. Please try again."
        );
        return;
      }
    } finally {
      setLoading(false);
    }
  }, [plantId, startDate, endDate, triggerSnackbar, handleLogout]);

  const employees: TimesheetEmployeeRow[] = report?.employees ?? [];
  const weekTotal = report?.week_total ?? 0;

  // pass derived plant name (for the <h2>) like original page
  const plantName = useMemo(
    () => plants.find((p) => p.id === (report?.plant_id ?? plantId))?.value,
    [plants, plantId, report?.plant_id]
  );

  return (
    <div>
      <PlantTimesheetReportView
        plants={plants}
        plantId={plantId}
        plantName={plantName}
        startDate={startDate}
        endDate={endDate}
        employees={employees}
        weekTotal={employees.length ? weekTotal : undefined}
        loading={loading}
        error={error}
        emailSendSuccess={emailSendSuccess}
        onPlantChange={(id) => setPlantId(id)}
        onStartDateChange={handleStartChange}
        onEndDateChange={handleEndChange}
        onSubmit={handleSubmit}
        onSendConfirm={handleSendConfirm}
      />
    </div>
  );
}
